import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import styles from './dropdown-content.scss?inline';
import { ref, createRef } from 'lit/directives/ref.js';
import { setPositionRelativeElement } from '@/helpers';

export default class SotbitDropdownContent extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        useTeleport: { type: Boolean, attribute: 'use-teleport' },
        variant: { type: String },
        disableAutoHide: { type: Boolean, attribute: 'disable-auto-hide' },
        /** Keep open on page scroll. */
        disableCloseOnScroll: { type: Boolean, attribute: 'disable-close-on-scroll' },
    };

    constructor() {
        super();

        this.useTeleport = false;
        this.variant = null;
        this._dropdown = null;
        this._relativeElement = null;
        this._scrollPanelRef = createRef();
        this.disableAutoHide = false;
        this.disableCloseOnScroll = false;
    }

    connectedCallback() {
        super.connectedCallback();

        this._setPosition();
        document.addEventListener('scroll', this._scrollHandler, {
            passive: true,
        });
        window.addEventListener('scroll', this._scrollHandler, {
            passive: true,
        });
        this.addEventListener('wheel', this._wheelHandler, { passive: false });

        this.setAttribute('connected', '');
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        document.removeEventListener('scroll', this._scrollHandler);
        window.removeEventListener('scroll', this._scrollHandler);
        this.removeEventListener('wheel', this._wheelHandler);

        this.removeAttribute('connected', '');
    }

    getDropdown() {
        return this._dropdown;
    }

    setDropdown(dropdown) {
        this._dropdown = dropdown;
    }

    getRelativeElement() {
        return this._relativeElement;
    }

    setRelativeElement(relativeElement) {
        this._relativeElement = relativeElement;
    }

    /**
     * Prefer parent dropdown helpers when teleported; fall back to own attrs.
     *
     * @returns {boolean}
     */
    _shouldCloseOnPageScroll() {
        if (typeof this._dropdown?.shouldCloseOnScroll === 'function') {
            return this._dropdown.shouldCloseOnScroll();
        }

        return !this.disableAutoHide && !this.disableCloseOnScroll;
    }

    _setPosition() {
        if (!this.useTeleport) {
            return;
        }

        setPositionRelativeElement(this, this._relativeElement || this._dropdown);
    }

    /**
     * Keeps the page from scrolling while the pointer is over this popup.
     * Chained page scroll closes the dropdown via `_scrollHandler`.
     *
     * @param {WheelEvent} event
     */
    _wheelHandler = (event) => {
        if (event.deltaY === 0) {
            return;
        }

        const scrollEl = this._scrollPanelRef?.value?.simpleBarInstance?.getScrollElement?.();
        const overScrollEl = !!scrollEl && event.composedPath().includes(scrollEl);

        if (!overScrollEl) {
            event.preventDefault();
            return;
        }

        const maxScrollTop = scrollEl.scrollHeight - scrollEl.clientHeight;
        const atTop = scrollEl.scrollTop <= 1;
        const atBottom = scrollEl.scrollTop >= maxScrollTop - 1;

        if (maxScrollTop <= 1 || (event.deltaY < 0 && atTop) || (event.deltaY > 0 && atBottom)) {
            event.preventDefault();
        }
    };

    _scrollHandler = (e) => {
        if (!this._shouldCloseOnPageScroll()) {
            return;
        }

        if (!this._scrollPanelRef?.value) {
            return;
        }

        if (
            e.target === document ||
            (!e.composedPath()?.includes(this._scrollPanelRef?.value) &&
                !e.composedPath()?.includes(this) &&
                !e.detail?.originalEvent?.composedPath()?.includes(this) &&
                !e.detail?.originalEvent?.composedPath()?.includes(this._scrollPanelRef?.value))
        ) {
            this._dropdown?.hide();
        }
    };

    _handleSlotChange = (e) => {
        if (!this.useTeleport || !this._dropdown) {
            return;
        }

        this._dropdown._dropdownItems = e.target.assignedNodes({ flatten: true });
    };

    render() {
        return html`
            <slot name="header"></slot>
            <div class="scroll-wrapper">
                <sotbit-scroll-panel
                    class="scroll-panel"
                    part="scroll-panel"
                    dropdown-variant="${this.variant || nothing}"
                    variant="dropdown"
                    ${ref(this._scrollPanelRef)}
                >
                    <div class="content" part="content">
                        <slot @slotchange="${this._handleSlotChange}"></slot>
                    </div>
                </sotbit-scroll-panel>
            </div>
            <slot name="footer"></slot>
        `;
    }
}
